import { action, computed, observable } from 'mobx'
import {
  type IValidationSchema,
  type IValidator,
  required,
  validationContext,
} from 'validx'
import type { AnswerOptions } from '@/features/legends/editor/entities'
import {
  type UnknownElement,
  UnknownElementError,
} from '@/features/legends/editor/legend-document'
import { Types } from '@/features/legends/editor/ryno-types'
import type { EffectEditorViewModel } from '@/features/legends/effects/EffectEditorViewModel'
import {
  type EffectDescriptor,
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type {
  NumericFormat,
  NumericQuestion,
} from '@/features/legends/questions/questions'
import { validateObject } from '@/utils/validx-util'

/**
 * A number format the builder can edit.
 */
type KnownNumericFormat = Exclude<NumericFormat, UnknownElement>

const validPlaces: IValidator<NumericQuestionEditorViewModel> = ({ obj }) => {
  if (obj.places === null) {
    return true
  }
  if (obj.places < 0) {
    return 'Must not be a negative number'
  }
  if (obj.places > 10) {
    return 'Must be less than 10'
  }
  return true
}

/**
 * A question answered with a number, with an effect that can use it.
 */
export class NumericQuestionEditorViewModel extends QuestionEditorViewModelBase<NumericQuestion> {
  private readonly _schema: IValidationSchema<NumericQuestionEditorViewModel> =
    {
      ...QuestionEditorViewModelBase.sharedSchema,
      effect: [validateObject('There are invalid effects')],
      question: [required('Question is required')],
      places: [validPlaces],
    }

  readonly type = 'Numeric'

  /**
   * Runs when the question is answered.
   */
  readonly effect: EffectEditorViewModel

  @observable accessor question = 'New Numeric Question'
  @observable accessor description = ''
  @observable.ref accessor format: KnownNumericFormat = { type: 'Integer' }

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this.effect = this.createEffect()
    this.effect.setExpanded(true)
    this.lifecycle.manage(this.effect)
    this._validateWhileEditing(this._schema)
  }

  /** @inheritDoc */
  get valueType() {
    return Types.number
  }

  /** @inheritDoc */
  get answerOptions(): AnswerOptions {
    return { type: 'text' }
  }

  /** @inheritDoc */
  get displayText() {
    return this.question
  }

  /** @inheritDoc */
  @computed
  get effectDescriptors(): EffectDescriptor[] {
    return [{ id: 'effect', title: 'Effect', effect: this.effect }]
  }

  /**
   * Whether the answer can have decimals.
   */
  @computed
  get isDecimal() {
    return this.format.type === 'Decimal'
  }

  /**
   * The number of decimals, when the answer can have them.
   */
  @computed
  get places() {
    return this.format.type === 'Decimal' ? (this.format.places ?? null) : null
  }

  /**
   * Allows or disallows decimals. Decimals start out with 2 places.
   */
  @action.bound
  setDecimal(isDecimal: boolean) {
    this.format = isDecimal
      ? { type: 'Decimal', places: 2 }
      : { type: 'Integer' }
  }

  /**
   * Sets the number of decimals.
   */
  @action.bound
  setPlaces(places: number | null) {
    this.format = { type: 'Decimal', places: places ?? 0 }
  }

  /** @inheritDoc */
  @action
  hydrate(question: NumericQuestion) {
    if (question.format?.type === 'Unknown') {
      throw new UnknownElementError('number format', question.format)
    }
    this._hydrateShared(question)
    this.question = question.question ?? this.question
    this.description = question.description ?? ''
    // Kept as stored, so loading doesn't change the document.
    this.format = question.format ?? { type: 'Integer' }
    this.effect.hydrate(question.effect)
  }

  /** @inheritDoc */
  protected _serialize(): NumericQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      question: this.question,
      description: this.description,
      format: this.format,
      effect: this.effect.toJSON(),
    }
  }
}
